<template>
  <div class="form-section mb-0">
    <div class="flex justify-between items-center mb-3">
      <h2 class="form-subtitle mb-0">Legajo digital</h2>
      <wz-button variant="primary" @click="openUploadModal">
        <i class="ri-upload-2-line mr-1"></i> Subir documento
      </wz-button>
    </div>

    <!-- Tabla de documentos -->
    <div v-if="documents.length === 0 && !loading" class="text-center py-4 text-muted">
      No hay documentos registrados
    </div>

    <div v-if="documents.length > 0" class="overflow-x-auto">
    <table class="wz-table w-full">
      <thead>
        <tr>
          <th>Tipo</th>
          <th>Archivo</th>
          <th>Ext.</th>
          <th>Tamaño</th>
          <th>Fecha subida</th>
          <th>Observaciones</th>
          <th>Opciones</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="doc in documents" :key="doc.id">
          <td>{{ doc.documentType ? doc.documentType.name : '—' }}<small v-if="doc.documentStatus === 'FIRMADO'" class="block text-muted">Firmado</small></td>
          <td>{{ doc.fileName }}</td>
          <td>{{ doc.fileExtension }}</td>
          <td>{{ formatSize(doc.fileSize) }}</td>
          <td>{{ formatDate(doc.createdAt) }}</td>
          <td>{{ doc.notes || '—' }}</td>
          <td class="cell-center d_option md:pr-2 relative">
            <wz-dropdown
              :items="[
                { key: 'preview',  label: 'Ver' },
                { key: 'download', label: 'Descargar' },
                { key: 'delete',   label: 'Archivar', danger: true },
              ]"
              placement="left"
              @click="handleDocAction(doc, $event)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    </div>

    <!-- Documentos de cese (de los contratos cesados del colaborador) -->
    <div v-if="cessationContracts.length || cessationDocuments.length" class="mt-5 pt-4" style="border-top:1px solid var(--admin-border-color)">
      <div class="flex justify-between items-center mb-3">
        <h2 class="form-subtitle mb-0">Documentos de cese</h2>
        <wz-button v-if="cessationContracts.length" variant="primary" @click="openCessationUploadModal">
          <i class="ri-upload-2-line mr-1"></i> Subir documento de cese
        </wz-button>
      </div>

      <div v-if="cessationDocuments.length === 0" class="text-center py-3 text-muted">
        No hay documentos de cese registrados
      </div>
      <div v-else class="overflow-x-auto">
      <table class="wz-table w-full">
        <thead>
          <tr>
            <th>Archivo</th>
            <th>Contrato</th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="doc in cessationDocuments" :key="doc.documentId">
            <td>{{ doc.fileName }}</td>
            <td>{{ doc.contractNumber || '—' }}</td>
            <td class="cell-center d_option md:pr-2 relative">
              <wz-button variant="outline" size="sm" class="mr-2" @click="downloadCessationDoc(doc)">
                <i class="ri-download-2-line mr-1"></i>Descargar
              </wz-button>
              <wz-button variant="outline" size="sm" @click="openReplaceModal(doc)">
                <i class="ri-refresh-line mr-1"></i>Reemplazar
              </wz-button>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>

    <!-- DEF-016: Modal de reemplazo de documento de cese -->
    <wz-modal v-model="showReplaceModal" title="Reemplazar documento de cese" @close="resetReplaceForm">
      <wz-alert variant="warning" icon="ri-error-warning-line" class="mb-3">
        El archivo actual (<strong>{{ replaceTarget ? replaceTarget.fileName : '' }}</strong>) se conservará
        como archivado y el nuevo quedará como documento activo del cese.
      </wz-alert>
      <div class="mb-3">
        <label>Nuevo archivo * <small class="text-muted">(PDF, JPG, JPEG, PNG — máx. 10MB)</small></label>
        <wz-file-input
          :key="replaceFileKey"
          class="w-full mt-2"
          id-file="cessation-replace-upload"
          :accept-file="['.pdf', '.jpg', '.jpeg', '.png']"
          @onChange="onReplaceFileChange"
        />
      </div>
      <template #footer>
        <wz-button variant="light" @click="showReplaceModal = false">Cancelar</wz-button>
        <wz-button variant="primary" :disabled="!replaceForm.file || replaceUploading" @click="confirmReplace">
          {{ replaceUploading ? 'Reemplazando...' : 'Reemplazar' }}
        </wz-button>
      </template>
    </wz-modal>

    <!-- Documentos archivados: colapsado por defecto; no se muestra si no hay ninguno -->
    <div v-if="archivedDocuments.length > 0" class="mt-5 pt-4" style="border-top:1px solid var(--admin-border-color)">
      <button
        type="button"
        class="flex items-center gap-1 form-subtitle mb-0 cursor-pointer"
        :aria-expanded="showArchived ? 'true' : 'false'"
        aria-controls="archived-documents"
        @click="showArchived = !showArchived"
      >
        <i :class="showArchived ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'" aria-hidden="true"></i>
        {{ archivedDocuments.length }} {{ archivedDocuments.length === 1 ? 'documento archivado' : 'documentos archivados' }}
      </button>

      <div v-show="showArchived" id="archived-documents" class="overflow-x-auto mt-3">
      <table class="wz-table w-full">
        <thead>
          <tr>
            <th>Tipo</th>
            <th>Nombre de archivo</th>
            <th>Fecha de archivado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="doc in archivedDocuments" :key="doc.id">
            <td>{{ doc.documentType ? doc.documentType.name : '—' }}</td>
            <td>{{ doc.fileName }}</td>
            <td>{{ formatDate(doc.archivedAt) }}</td>
            <td class="cell-center d_option md:pr-2 relative">
              <wz-button variant="outline" size="sm" @click="downloadDoc(doc, true)">
                <i class="ri-download-2-line mr-1"></i>Descargar
              </wz-button>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>

    <!-- Modal de carga -->
    <wz-modal
      v-model="showUploadModal"
      title="Subir documento"
      @close="resetUploadForm"
    >
      <div class="mb-3">
        <label>Tipo de documento *</label>
        <wz-select
          id="uploadDocumentType"
          v-model="uploadForm.documentTypeId"
          :items="availableDocumentTypes"
          defaultOption="Elegir tipo de documento"
        />
      </div>

      <div class="mb-3">
        <label>Archivo * <small class="text-muted">(PDF, JPG, JPEG, PNG — máx. 25MB)</small></label>
        <wz-file-input
          class="w-full mt-2"
          id-file="employee-doc-upload"
          :accept-file="['.pdf', '.jpg', '.jpeg', '.png']"
          @onChange="onFileChange"
        />
      </div>

      <div class="mb-3">
        <label class="label">Observaciones</label>
        <textarea
          v-model="uploadForm.notes"
          class="wz-form-control w-full"
          rows="3"
          placeholder="Ingresar observaciones (opcional)"
        />
      </div>

      <UploadSignedQuestion
        v-model="uploadForm.signed"
        :required="uploadTypeRequiresSignature"
        :type-id="uploadForm.documentTypeId"
      />

      <wz-alert v-if="allTypesUploaded" variant="info" icon="ri-checkbox-circle-line" class="mb-3">
        Todos los tipos de documento ya han sido cargados.
      </wz-alert>

      <div class="flex justify-end mt-3">
        <wz-button variant="light" class="mr-2" @click="showUploadModal = false">
          Cancelar
        </wz-button>
        <wz-button variant="primary" @click="uploadDocument" :disabled="uploading || allTypesUploaded">
          {{ uploading ? 'Subiendo...' : 'Subir' }}
        </wz-button>
      </div>
    </wz-modal>

    <!-- Modal de preview -->
    <wz-modal
      v-model="showPreviewModal"
      :title="previewTitle"
      size="xl"
      @close="onPreviewHidden"
    >
      <div class="text-center">
        <img
          v-if="previewType === 'image'"
          :src="previewUrl"
          class="img-fluid"
          style="max-height: 75vh"
        />
        <embed
          v-else-if="previewType === 'pdf'"
          :src="previewUrl"
          type="application/pdf"
          width="100%"
          style="height: 75vh"
        />
      </div>
    </wz-modal>

    <!-- Modal de subida de documento de cese -->
    <wz-modal
      v-model="showCessationModal"
      title="Subir documento de cese"
      @close="resetCessationForm"
    >
      <div class="mb-3">
        <label>Contrato cesado *</label>
        <wz-select
          v-model="cessationForm.contractId"
          :items="cessationContractOptions"
          defaultOption="Elegir contrato"
        />
      </div>

      <div class="mb-3">
        <label>Archivo * <small class="text-muted">(PDF, JPG, JPEG, PNG — máx. 10MB)</small></label>
        <wz-file-input
          :key="cessationFileKey"
          class="w-full mt-2"
          id-file="cessation-doc-upload"
          :accept-file="['.pdf', '.jpg', '.jpeg', '.png']"
          @onChange="onCessationFileChange"
        />
      </div>

      <div class="flex justify-end mt-3">
        <wz-button variant="light" class="mr-2" @click="showCessationModal = false">
          Cancelar
        </wz-button>
        <wz-button variant="primary" @click="uploadCessationDoc" :disabled="cessationUploading">
          {{ cessationUploading ? 'Subiendo...' : 'Subir' }}
        </wz-button>
      </div>
    </wz-modal>

    <wz-confirm-modal
      v-model="deleteModal"
      title="Archivar documento"
      description="¿Archivar este documento? Saldrá de la lista activa pero quedará en el historial, podrá recuperarse desde la sección de archivados."
      accept-text="Archivar"
      accept-variant="danger"
      @confirm="onDeleteConfirmed"
    />
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { DocumentTypeService, EmployeeDocumentService, ContractsService } from '@/core/services'
import fileDownload from 'js-file-download'
import UploadSignedQuestion from '../../components/UploadSignedQuestion.vue'

export default {
  components: { UploadSignedQuestion },
  props: {
    employeeId: { type: Number, default: null },
    // Documentos de cese (de contratos cesados), aplanados desde la ficha: { contractId, contractNumber, documentId, fileName }
    cessationDocuments: { type: Array, default: () => [] },
    // Contratos cesados del colaborador: { contractId, contractNumber } — destinos posibles al subir.
    cessationContracts: { type: Array, default: () => [] },
  },
  emits: ['compliance-changed', 'cessation-uploaded'],
  data() {
    return {
      loading: false,
      uploading: false,
      documents: [],
      // Documentos archivados (soft-delete; el archivo se conserva y se puede descargar)
      archivedDocuments: [],
      showArchived: false,
      documentTypeList: [],
      showUploadModal: false,
      uploadForm: {
        documentTypeId: null,
        file: null,
        notes: '',
        signed: false,
      },
      // Preview
      showPreviewModal: false,
      previewUrl:       null,
      previewType:      null,
      previewTitle:     null,
      // Delete confirm
      deleteModal:      false,
      deleteTarget:     null,
      // Subida de documento de cese
      showCessationModal: false,
      cessationUploading: false,
      cessationFileKey:   0,
      cessationForm:    { contractId: null, file: null },
      // DEF-016: reemplazo de documento de cese
      showReplaceModal:   false,
      replaceTarget:      null,
      replaceForm:        { file: null },
      replaceFileKey:     0,
      replaceUploading:   false,
    }
  },
  computed: {
    uploadedTypeIds() {
      return this.documents
        .filter(d => d.documentType)
        .map(d => d.documentType.id)
    },
    availableDocumentTypes() {
      return this.documentTypeList.filter(t => !this.uploadedTypeIds.includes(t.key))
    },
    uploadTypeRequiresSignature() {
      const type = this.documentTypeList.find(t => t.key === this.uploadForm.documentTypeId)
      return !!(type && type.requiresSignature)
    },
    allTypesUploaded() {
      return this.documentTypeList.length > 0 && this.availableDocumentTypes.length === 0
    },
    cessationContractOptions() {
      return this.cessationContracts.map(c => ({
        key: c.contractId,
        label: c.contractNumber || `Contrato ${c.contractId}`,
      }))
    },
  },
  watch: {
    employeeId(val) {
      if (val) {
        this.loadDocuments()
        this.loadArchivedDocuments()
      }
    },
  },
  methods: {
    async loadDocuments() {
      if (!this.employeeId) return
      try {
        this.loading = true
        this.documents = (await EmployeeDocumentService.list(this.employeeId)).payload || []
      } catch (ex) {
        console.error(ex)
      } finally {
        this.loading = false
      }
    },
    async loadArchivedDocuments() {
      if (!this.employeeId) return
      try {
        this.archivedDocuments = (await EmployeeDocumentService.listArchived(this.employeeId)).payload || []
      } catch (ex) {
        console.error(ex)
      }
    },
    async loadDocumentTypes() {
      try {
        const resp = (await DocumentTypeService.list()).payload || []
        this.documentTypeList = resp.map(d => ({ key: d.id, label: d.name, requiresSignature: d.requiresEmployeeSignature }))
      } catch (ex) {
        console.error(ex)
      }
    },
    handleDocAction(doc, item) {
      if (item.key === 'preview')  this.previewDoc(doc)
      if (item.key === 'download') this.downloadDoc(doc)
      if (item.key === 'delete')   this.confirmDelete(doc)
    },
    onFileChange(files) {
      this.uploadForm.file = files && files.length > 0 ? files[0] : null
    },
    async uploadDocument() {
      if (!this.uploadForm.documentTypeId) {
        Alert.error('Selecciona el tipo de documento')
        return
      }
      if (!this.uploadForm.file) {
        Alert.error('Selecciona un archivo')
        return
      }
      if (this.uploadForm.signed === null) {
        Alert.error('Indica si el archivo ya está firmado por el colaborador')
        return
      }
      try {
        this.uploading = true
        const formData = new FormData()
        formData.append('employeeId', this.employeeId)
        formData.append('documentTypeId', this.uploadForm.documentTypeId)
        formData.append('file', this.uploadForm.file)
        if (this.uploadForm.notes) formData.append('notes', this.uploadForm.notes)
        formData.append('signed', this.uploadForm.signed)
        await EmployeeDocumentService.upload(formData)
        Alert.success('Documento subido correctamente')
        this.showUploadModal = false
        await this.loadDocuments()
        this.$emit('compliance-changed')
      } catch (ex) {
        Alert.error(ex.errors ? ex.errors.message : 'Error al subir el documento')
        console.error(ex)
      } finally {
        this.uploading = false
      }
    },
    async previewDoc(doc) {
      const ext = (doc.fileExtension || '').toLowerCase()
      const isImage = ['jpg', 'jpeg', 'png'].includes(ext)
      const isPdf   = ext === 'pdf'

      if (!isImage && !isPdf) {
        Alert.error('Formato no soportado para vista previa. Usa el botón Descargar.')
        return
      }

      const mimeTypes = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', pdf: 'application/pdf' }
      const mimeType = mimeTypes[ext] || 'application/octet-stream'

      try {
        const response = await EmployeeDocumentService.download(doc.id)
        const blob = response instanceof Blob ? response : new Blob([response.data || response], { type: mimeType })
        this.previewUrl   = window.URL.createObjectURL(blob)
        this.previewType  = isImage ? 'image' : 'pdf'
        this.previewTitle = doc.fileName
        this.showPreviewModal = true
      } catch (ex) {
        Alert.error('Error al cargar la vista previa')
        console.error(ex)
      }
    },
    onPreviewHidden() {
      if (this.previewUrl) window.URL.revokeObjectURL(this.previewUrl)
      this.previewUrl  = null
      this.previewType = null
    },
    // archived = true para documentos archivados (mismo flujo de descarga, con includeArchived)
    async downloadDoc(doc, archived = false) {
      try {
        const response = await EmployeeDocumentService.download(doc.id, archived)
        const blob = response instanceof Blob ? response : response.data
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', doc.fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (ex) {
        Alert.error('Error al descargar el documento')
        console.error(ex)
      }
    },
    async downloadCessationDoc(doc) {
      try {
        const response = await ContractsService.downloadCessationDocument(doc.contractId, doc.documentId)
        fileDownload(response.data, doc.fileName || 'documento-cese')
      } catch (ex) {
        Alert.error('Error al descargar el documento de cese')
        console.error(ex)
      }
    },
    openCessationUploadModal() {
      this.resetCessationForm()
      // Si solo hay un contrato cesado, preseleccionarlo.
      if (this.cessationContracts.length === 1) {
        this.cessationForm.contractId = this.cessationContracts[0].contractId
      }
      this.showCessationModal = true
    },
    resetCessationForm() {
      this.cessationForm = { contractId: null, file: null }
      this.cessationFileKey += 1 // re-monta el wz-file-input para limpiar el archivo
    },
    onCessationFileChange(files) {
      this.cessationForm.file = files && files.length > 0 ? files[0] : null
    },
    async uploadCessationDoc() {
      if (!this.cessationForm.contractId) {
        Alert.error('Selecciona el contrato cesado')
        return
      }
      if (!this.cessationForm.file) {
        Alert.error('Selecciona un archivo')
        return
      }
      try {
        this.cessationUploading = true
        await ContractsService.uploadCessationDocument(this.cessationForm.contractId, this.cessationForm.file)
        Alert.success('Documento de cese subido correctamente')
        this.showCessationModal = false
        this.$emit('cessation-uploaded') // la ficha recarga para reflejar el nuevo documento
      } catch (ex) {
        Alert.error(ex.errors ? ex.errors.message : 'Error al subir el documento de cese')
        console.error(ex)
      } finally {
        this.cessationUploading = false
      }
    },
    // DEF-016: reemplazo de documento de cese
    openReplaceModal(doc) {
      this.replaceTarget = doc
      this.resetReplaceForm()
      this.showReplaceModal = true
    },
    resetReplaceForm() {
      this.replaceForm = { file: null }
      this.replaceFileKey += 1
    },
    onReplaceFileChange(files) {
      this.replaceForm.file = files && files.length > 0 ? files[0] : null
    },
    async confirmReplace() {
      if (!this.replaceTarget || !this.replaceForm.file) return
      try {
        this.replaceUploading = true
        await ContractsService.replaceCessationDocument(
          this.replaceTarget.contractId, this.replaceTarget.documentId, this.replaceForm.file)
        Alert.success('Documento de cese reemplazado (el anterior quedó archivado)')
        this.showReplaceModal = false
        this.$emit('cessation-uploaded') // la ficha recarga para reflejar el nuevo documento
      } catch (ex) {
        Alert.error(ex.errors ? ex.errors.message : 'Error al reemplazar el documento de cese')
        console.error(ex)
      } finally {
        this.replaceUploading = false
      }
    },
    confirmDelete(doc) {
      this.deleteTarget = doc
      this.deleteModal = true
    },
    async onDeleteConfirmed() {
      const doc = this.deleteTarget
      this.deleteModal = false
      if (!doc) return
      try {
        await EmployeeDocumentService.delete(doc.id)
        Alert.success('Documento archivado')
        await Promise.all([this.loadDocuments(), this.loadArchivedDocuments()])
        this.$emit('compliance-changed')
      } catch (ex) {
        Alert.error(ex.errors ? ex.errors.message : 'Error al archivar el documento')
      } finally {
        this.deleteTarget = null
      }
    },
    resetUploadForm() {
      this.uploadForm = { documentTypeId: null, file: null, notes: '', signed: false }
    },
    openUploadModal() {
      // Limpiar siempre antes de abrir: el cierre programático del modal no
      // dispara @close, por lo que el comentario podía conservar el valor previo.
      this.resetUploadForm()
      this.showUploadModal = true
    },
    formatDate(dateStr) {
      if (!dateStr) return '—'
      return new Date(dateStr).toLocaleDateString('es-PE')
    },
    formatSize(bytes) {
      if (!bytes) return '—'
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },
  },
  async created() {
    await this.loadDocumentTypes()
    if (this.employeeId) await Promise.all([this.loadDocuments(), this.loadArchivedDocuments()])
  },
}
</script>

<style lang="stylus" scoped></style>
